import { useState, useEffect, useCallback, useRef, useId } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ticketsApi, operatorsApi, templatesApi, sendFileToTicket, blockCitizen, unblockCitizen, categoriesApi, getApiBase } from '../api'
import { RichText, TelegramEmbeds, extractTelegramLinks } from '../components/TelegramEmbed'
import { useStore, useAuthStore } from '../store'
import {
  Alert, Avatar, Badge, Beacon, Button, Card, Code, ConfirmModal, DescriptionList,
  Dot, Field, Loader, Menu, Modal, Select, Spinner, Textarea,
} from '../ui'
import {
  HiArrowLeft, HiArrowPath, HiChatBubbleLeftRight, HiCheck, HiChevronDown, HiChevronUp,
  HiClock, HiDocumentText, HiEllipsisVertical, HiMicrophone,
  HiNoSymbol, HiPaperAirplane, HiPaperClip, HiShieldCheck, HiSparkles, HiStar, HiXMark,
} from 'react-icons/hi2'

function validateMessageLanguage(text, chatLang) {
  if (!text || !text.trim()) return { valid: true }

  const cleanText = text
    .replace(/https?:\/\/[^\s]+/g, '')
    .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '')
    .replace(/[^a-zA-Zа-яА-ЯёЁўЎқҚғҒҳҲ]/g, '')

  if (cleanText.length < 4) return { valid: true }

  let cyrCount = 0
  let latCount = 0
  for (let i = 0; i < cleanText.length; i++) {
    const char = cleanText[i]
    if (/[а-яА-ЯёЁўЎқҚғҒҳҲ]/.test(char)) {
      cyrCount++
    } else if (/[a-zA-Z]/.test(char)) {
      latCount++
    }
  }

  if (cyrCount === 0 && latCount === 0) return { valid: true }

  if (chatLang === 'uz') {
    if (cyrCount > latCount) {
      return {
        valid: false,
        error: "Murojaat tili O'zbekcha (Lotin). Iltimos, javobni lotin alifbosida yozing!"
      }
    }
  } else if (chatLang === 'ru') {
    if (latCount > cyrCount) {
      return {
        valid: false,
        error: "Язык обращения Русский. Пожалуйста, напишите ответ на кириллице!"
      }
    }
  } else if (chatLang === 'uz_cy') {
    if (latCount > cyrCount) {
      return {
        valid: false,
        error: "Murojaat tili Ўзбекча (Кирилл). Iltimos, javobni kirill alifbosida yozing!"
      }
    }
  }

  return { valid: true }
}

// ── Ro'yxatlar va yorliqlar ─────────────────────────────────────
const STATUS_OPT = [
  { value: 'new', label: 'Yangi' },
  { value: 'accepted', label: 'Qabul' },
  { value: 'in_progress', label: 'Jarayonda' },
  { value: 'waiting', label: 'Kutmoqda' },
  { value: 'resolved', label: 'Yechildi' },
  { value: 'closed', label: 'Yopildi' },
]

const PRI_OPT = [
  { value: 'critical', label: 'Kritik' },
  { value: 'high', label: 'Yuqori' },
  { value: 'medium', label: "O'rta" },
  { value: 'low', label: 'Past' },
]

// Fuqaroning bot tili — bayroq emoji o'rniga toza matn (DESIGN.md 8-bo'lim)
const LANG_LABEL = { uz: 'Lotin', uz_cy: 'Kirill', ru: 'Ruscha' }

// Muloqot yakunlangan bo'lsa yozish ham, fayl yuborish ham to'siladi
const isLocked = (ticket) => ticket?.status === 'resolved' || ticket?.status === 'closed'

const fmtDateTime = (v) => {
  if (!v) return null
  const d = new Date(v)
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleString('uz-UZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// ── Fayl preview ────────────────────────────────────────────────
// VirusTotal holatlari: mantiq avvalgidek, ko'rinish kitning Badge'iga o'tdi.
// `score` — hisobni ko'rsatish qoidasi: 'clean' da 0/0 yashiriladi, 'any' da har doim chiqadi.
const VT_VIEW = {
  clean:        { tone: 'ok',       icon: HiShieldCheck,         text: 'VirusTotal: Xavfsiz',        score: 'clean', link: true },
  malicious:    { tone: 'critical', icon: Beacon, text: 'VirusTotal: Zararli!',       score: 'any',   link: true },
  suspicious:   { tone: 'medium',   icon: Beacon, text: 'VirusTotal: Shubhali',       score: 'any',   link: true },
  error:        { tone: 'neutral',  icon: Beacon, text: "VirusTotal: Tekshirib bo'lmadi" },
  error_no_key: { tone: 'critical', icon: Beacon, text: "VirusTotal: API kalit yo'q" },
  error_limit:  { tone: 'medium',   icon: Beacon, text: 'VirusTotal: Limit tugagan' },
}

function VtBadge({ file }) {
  const status = file.vt_status
  if (!status || status === 'none') return null

  // Tekshiruv davom etmoqda — miltillovchi ikonka o'rniga kit spinneri
  if (status.startsWith('pending') || status === 'scanning') {
    return (
      <Badge tone="neutral" title="VirusTotal tekshirmoqda...">
        <Spinner size={12} label="Tekshirilmoqda" />
        VirusTotal tekshirilmoqda...
      </Badge>
    )
  }

  const view = VT_VIEW[status]
  if (!view) return null

  const showScore = view.score === 'any'
    ? Boolean(file.vt_score)
    : view.score === 'clean'
      ? Boolean(file.vt_score && file.vt_score !== '0/0')
      : false
  const text = showScore ? `${view.text} (${file.vt_score})` : view.text

  const clickable = Boolean(view.link && file.vt_link)
  const open = () => { if (file.vt_link) window.open(file.vt_link, '_blank') }

  return (
    <Badge
      tone={view.tone}
      icon={view.icon}
      title={clickable ? "VirusTotal hisobotini ko'rish" : text}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      style={clickable ? { cursor: 'pointer' } : undefined}
      onClick={clickable ? (e) => { e.preventDefault(); e.stopPropagation(); open() } : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open() } } : undefined}
    >
      {text}
    </Badge>
  )
}

function FilePreview({ file }) {
  const isImage = file.content_type?.startsWith('image/')
  const isVideo = file.content_type?.startsWith('video/')
  const isAudio = file.content_type?.startsWith('audio/')
  const BASE  = getApiBase()
  const [src, setSrc] = useState(null)

  useEffect(() => {
    let objUrl = null
    const token = localStorage.getItem('access_token') || ''
    fetch(`${BASE}/files/${file.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.blob() : null)
      .then(b => { if (b) { objUrl = URL.createObjectURL(b); setSrc(objUrl) } })
      .catch(() => {})
    return () => { if (objUrl) URL.revokeObjectURL(objUrl) }
  }, [file.id])

  const size = file.file_size
    ? file.file_size > 1048576 ? `${(file.file_size/1048576).toFixed(1)}MB` : `${(file.file_size/1024).toFixed(0)}KB`
    : ''

  // Ilova qora (operator) va oq (fuqaro) pufak ichida ham bir xil o'qilishi uchun
  // neytral yuzada chiziladi — rang ma'lumot tashimaydi.
  const attachStyle = {
    alignItems: 'flex-start',
    marginTop: 'var(--ui-sp-8)',
    padding: 'var(--ui-sp-8)',
    border: '1px solid var(--ui-border)',
    borderRadius: 'var(--ui-r)',
    background: 'var(--ui-surface)',
    color: 'var(--ui-text-1)',
  }
  const capStyle = { fontSize: 'var(--ui-fs-xs)', color: 'var(--ui-text-2)' }

  if (isImage && src) return (
    <div style={attachStyle} className="ui-col ui-gap-4">
      <img src={src} alt={file.original_name} onClick={() => window.open(src, '_blank')}
        style={{ maxWidth: 240, maxHeight: 180, borderRadius: 'var(--ui-r)', cursor: 'pointer', objectFit: 'cover' }} />
      <span style={capStyle}>{file.original_name} {size}</span>
      <VtBadge file={file} />
    </div>
  )
  if (isVideo && src) return (
    <div style={attachStyle} className="ui-col ui-gap-4">
      <video src={src} controls style={{ maxWidth: 240, borderRadius: 'var(--ui-r)' }} />
      <VtBadge file={file} />
    </div>
  )
  if (isAudio && src) return (
    <div style={attachStyle} className="ui-col ui-gap-4">
      <span style={capStyle}>{file.original_name}</span>
      <VtBadge file={file} />
      <audio src={src} controls style={{ width: '100%', height: 32 }} />
    </div>
  )
  return (
    <div style={attachStyle} className="ui-col ui-gap-4">
      <a href={src || '#'} download={file.original_name} target="_blank" rel="noreferrer"
        className="ui-row ui-gap-8"
        style={{ color: 'var(--ui-text-1)', fontSize: 'var(--ui-fs-sm)', textDecoration: 'none' }}>
        <HiDocumentText aria-hidden="true" style={{ width: 20, height: 20, color: 'var(--ui-text-2)' }} />
        <span>
          {file.original_name}{' '}
          {size && <span className="ui-muted" style={{ fontSize: 'var(--ui-fs-xs)' }}>({size})</span>}
        </span>
      </a>
      <VtBadge file={file} />
    </div>
  )
}

// ── Xabar ostidagi AI izohi ─────────────────────────────────────
// Qaysi provayder/model javob yozgani va uning narxi. Ochiladigan tafsilot.
function AiMetaNote({ meta, align }) {
  const [open, setOpen] = useState(false)
  const shortModel = meta.model?.includes('/') ? meta.model.split('/').pop() : meta.model

  return (
    <div className="ui-col ui-gap-4" style={{ alignItems: align, marginBottom: 'var(--ui-sp-4)' }}>
      <Button
        variant="ghost"
        size="sm"
        icon={HiSparkles}
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o) }}
        title="Ushbu xabar uchun ishlatilgan AI va model ma'lumotlari"
      >
        {meta.provider}
        <Code>{shortModel}</Code>
        <span className="ui-nums">${(meta.cost_usd || 0).toFixed(5)}</span>
        {open ? <HiChevronUp aria-hidden="true" /> : <HiChevronDown aria-hidden="true" />}
      </Button>

      {open && (
        <div style={{
          minWidth: 230,
          maxWidth: 320,
          padding: 'var(--ui-sp-8) var(--ui-sp-12)',
          border: '1px solid var(--ui-border)',
          borderRadius: 'var(--ui-r-lg)',
          background: 'var(--ui-surface)',
        }}>
          <DescriptionList items={[
            { key: 'Model', value: <Code>{meta.model}</Code> },
            { key: 'Xarajat', value: <span className="ui-nums">${(meta.cost_usd || 0).toFixed(6)}</span> },
            { key: 'Tokenlar', value: <span className="ui-nums">{(meta.total_tokens || 0).toLocaleString()} ta</span> },
            { key: 'Kirish / Chiqish', value: <span className="ui-nums">{meta.input_tokens} / {meta.output_tokens}</span> },
          ]} />
        </div>
      )}
    </div>
  )
}

// ── Suhbat sarlavhasi: fuqaro, tili, amallar menyusi ────────────
function ChatHeader({ ticket, onCloseChat }) {
  const citizen = ticket?.citizen
  const name    = citizen?.full_name || 'Fuqaro'
  const lang    = citizen?.language_code

  const [blocked, setBlocked]     = useState(citizen?.is_blocked || false)
  const [blocking, setBlocking]   = useState(false)
  const [showBlock, setShowBlock] = useState(false)
  const [reason, setReason]       = useState('')

  const doBlock = async () => {
    setBlocking(true)
    try {
      await blockCitizen(ticket.citizen_id, reason)
      setBlocked(true)
      setShowBlock(false)
    } catch {}
    setBlocking(false)
  }

  const doUnblock = async () => {
    setBlocking(true)
    try {
      await unblockCitizen(ticket.citizen_id)
      setBlocked(false)
    } catch {}
    setBlocking(false)
  }

  const items = []
  if (ticket) {
    items.push(blocked
      ? { label: 'Blokdan chiqarish', icon: HiCheck, onClick: doUnblock, disabled: blocking }
      : { label: 'Foydalanuvchini bloklash', icon: HiNoSymbol, onClick: () => setShowBlock(true), disabled: blocking })
  }
  if (onCloseChat) {
    items.push({ sep: true })
    items.push({ label: 'Chatni tugatish', icon: HiXMark, onClick: onCloseChat, danger: true })
  }

  return (
    <>
      <div className="ui-between" style={{
        flexShrink: 0,
        gap: 'var(--ui-sp-12)',
        padding: 'var(--ui-sp-12) var(--ui-sp-16)',
        borderBottom: '1px solid var(--ui-border)',
      }}>
        <div className="ui-row ui-gap-12" style={{ minWidth: 0 }}>
          <Avatar name={name} size={40} />
          <div style={{ minWidth: 0 }}>
            <div className="ui-row ui-gap-8 ui-wrap">
              <span style={{ fontSize: 'var(--ui-fs-md)', fontWeight: 600 }}>{name}</span>
              {ticket?.priority === 'critical' && <Badge tone="critical">Shoshilinch</Badge>}
              {blocked && <Badge tone="neutral" icon={HiNoSymbol}>Bloklangan</Badge>}
              {lang && <Badge tone="neutral">{LANG_LABEL[lang] || lang.toUpperCase()}</Badge>}
            </div>
            <div className="ui-row ui-gap-8" style={{ fontSize: 'var(--ui-fs-xs)', color: 'var(--ui-text-3)' }}>
              {citizen?.username ? <span className="ui-truncate">@{citizen.username}</span> : null}
              {ticket?.ticket_number && <span className="ui-mono">#{ticket.ticket_number}</span>}
            </div>
          </div>
        </div>

        {items.length > 0 && (
          <Menu
            align="end"
            items={items}
            trigger={<Button variant="ghost" icon={HiEllipsisVertical} iconOnly aria-label="Boshqa amallar" />}
          />
        )}
      </div>

      <Modal
        open={showBlock}
        onClose={() => setShowBlock(false)}
        title="Foydalanuvchini bloklash"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowBlock(false)} disabled={blocking}>Bekor qilish</Button>
            <Button variant="danger" onClick={doBlock} loading={blocking}>Bloklash</Button>
          </>
        }
      >
        <Field label="Sabab (ixtiyoriy)" hint="Bloklangan foydalanuvchi botga xabar yubora olmaydi.">
          <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Sabab..." style={{ minHeight: 72 }} />
        </Field>
      </Modal>
    </>
  )
}

// ── Yon ustun: boshqaruv va ma'lumot ────────────────────────────
function TicketSidebar({ ticket, categories = [], operators = [], isAdmin, onUpdate }) {
  const uid = useId()
  const citizen = ticket?.citizen

  const catOptions = [
    { value: '', label: "Kategoriya yo'q" },
    ...categories.map(c => ({ value: String(c.id), label: c.name_uz })),
  ]
  const opOptions = [
    { value: '', label: 'Tayinlanmagan' },
    ...operators.map(o => ({ value: String(o.id), label: o.full_name })),
  ]

  const info = [
    { key: 'Raqam', value: <span className="ui-mono">#{ticket?.ticket_number}</span> },
    { key: 'Fuqaro', value: citizen?.full_name },
    { key: 'Telegram', value: citizen?.username ? `@${citizen.username}` : null },
    { key: 'Telefon', value: citizen?.phone },
    { key: 'Til', value: citizen?.language_code ? (LANG_LABEL[citizen.language_code] || citizen.language_code.toUpperCase()) : null },
    { key: 'Tuman', value: ticket?.citizen_tuman },
    { key: 'Mahalla', value: ticket?.citizen_mahalla },
    { key: 'Murojaatlari', value: citizen?.total_tickets ? <span className="ui-nums">{citizen.total_tickets} ta</span> : null },
    { key: 'Operator', value: ticket?.operator?.full_name || 'Tayinlanmagan' },
    { key: 'Yaratilgan', value: fmtDateTime(ticket?.created_at) },
  ].filter(it => it.value)

  return (
    <div className="ui-col ui-gap-16">
      <Card title="Boshqaruv">
        <div className="ui-col ui-gap-12">
          <Field label="Holat" htmlFor={`${uid}-status`}>
            <Select id={`${uid}-status`} options={STATUS_OPT}
              value={ticket?.status || ''}
              onChange={e => onUpdate({ status: e.target.value })} />
          </Field>

          <Field label="Muhimlik" htmlFor={`${uid}-pri`}>
            <Select id={`${uid}-pri`} options={PRI_OPT}
              value={ticket?.priority || ''}
              onChange={e => onUpdate({ priority: e.target.value })} />
          </Field>

          {categories.length > 0 && (
            <Field label="Kategoriya" htmlFor={`${uid}-cat`}>
              <Select id={`${uid}-cat`} options={catOptions}
                value={ticket?.category_id ? String(ticket.category_id) : ''}
                onChange={e => onUpdate({ category_id: e.target.value || null })} />
            </Field>
          )}

          {isAdmin && operators.length > 0 && (
            <Field label="Operator" htmlFor={`${uid}-op`}>
              <Select id={`${uid}-op`} options={opOptions}
                value={ticket?.operator_id ? String(ticket.operator_id) : (ticket?.operator?.id ? String(ticket.operator.id) : '')}
                onChange={e => onUpdate({ operator_id: e.target.value || null })} />
            </Field>
          )}
        </div>
      </Card>

      {info.length > 0 && (
        <Card title="Ma'lumot">
          <DescriptionList items={info} />
        </Card>
      )}
    </div>
  )
}

// ── Suhbat ustuni: xabarlar + yuborish paneli ───────────────────
function ChatView({ ticket, messages, isAdmin, onSend, onCloseChat, pendingCount, ticketId }) {
  const [reply, setReply]         = useState('')
  const [sending, setSending]     = useState(false)
  const [notice, setNotice]       = useState(null)
  const [recording, setRecording] = useState(false)
  const [recSec, setRecSec]       = useState(0)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState('')

  const bottomRef    = useRef(null)
  const fileInputRef = useRef(null)
  const mediaRef     = useRef(null)
  const chunksRef    = useRef([])
  const timerRef     = useRef(null)
  const textareaRef  = useRef(null)

  const locked = isLocked(ticket)

  // FAQ panelidan "Chatga qo'yish" bosilganda matnni javob maydoniga qo'shish
  useEffect(() => {
    const h = e => {
      const t = e.detail?.text
      if (t) setReply(p => (p ? p + "\n" + t : t))
    }
    window.addEventListener('faq:insert', h)
    return () => window.removeEventListener('faq:insert', h)
  }, [])

  // Matn maydoni yozuv hajmiga qarab o'sadi (200px gacha)
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`
    }
  }, [reply])

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  const send = async () => {
    if (!reply.trim()) return
    setSending(true)
    setNotice(null)
    try {
      await onSend(reply)
      setReply('')
    } catch (e) {
      // Til tekshiruvi va boshqa xatolar shu yerda Alert bo'lib ko'rinadi
      setNotice(e?.message || "Xabar yuborilmadi")
    } finally {
      setSending(false)
    }
  }

  // ── Fayl yuklash ──────────────────────────────────────────────
  const handleFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 50 * 1024 * 1024) {
      setNotice("Fayl 50MB dan katta bo'lmasligi kerak")
      e.target.value = ''
      return
    }
    const type = file.type.startsWith('image/') ? 'image'
               : file.type.startsWith('video/') ? 'video'
               : file.type.startsWith('audio/') ? 'audio'
               : 'document'
    setUploading(true)
    setNotice(null)
    setUploadProgress(file.name.length > 25 ? file.name.slice(0, 25) + '...' : file.name)
    try {
      await sendFileToTicket(ticketId, file, type, '', file.name)
      await onSend('__reload__')
    } catch (err) { setNotice('' + err.message) }
    finally { setUploading(false); setUploadProgress(''); e.target.value = '' }
  }

  // ── Ovoz yozish ───────────────────────────────────────────────
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      // Brauzer qo'llab-quvvatlagan formatni aniqlash
      const mtype = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
        ? 'audio/ogg;codecs=opus'
        : 'audio/ogg'
      const mr = new MediaRecorder(stream, { mimeType: mtype })
      chunksRef.current = []
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      mr.start(200)
      mediaRef.current = mr
      setRecording(true)
      setRecSec(0)
      timerRef.current = setInterval(() => setRecSec(s => s + 1), 1000)
    } catch { setNotice("Mikrofonga kirish imkoni yo'q") }
  }

  const stopRecording = async () => {
    clearInterval(timerRef.current)
    setRecording(false)
    if (!mediaRef.current) return
    return new Promise((resolve) => {
      mediaRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mediaRef.current.mimeType })
        mediaRef.current.stream.getTracks().forEach(t => t.stop())
        if (blob.size > 0) {
          setUploading(true)
          setUploadProgress('Ovoz xabari yuborilmoqda...')
          try {
            // Format ga qarab extension aniqlash
            const ext = mediaRef.current.mimeType.includes('webm') ? 'voice.webm' : 'voice.ogg'
            await sendFileToTicket(ticketId, blob, 'voice', '', ext)
            await onSend('__reload__')
          } catch (e) { setNotice('' + e.message) }
          finally { setUploading(false); setUploadProgress('') }
        }
        resolve()
      }
      mediaRef.current.stop()
    })
  }

  const fmtSec  = `${Math.floor(recSec/60)}:${String(recSec%60).padStart(2,'0')}`
  const tgLinks = extractTelegramLinks(reply)
  const firstTg = tgLinks[0]

  return (
    <div className="ui-col ui-gap-12">
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 120px)',
        border: '1px solid var(--ui-border)',
        borderRadius: 'var(--ui-r-lg)',
        background: 'var(--ui-surface)',
        overflow: 'hidden',
      }}>
        <ChatHeader ticket={ticket} onCloseChat={onCloseChat} />

        {/* Fuqaro bahosi va fikr-taklifi */}
        {(ticket?.satisfaction_score || ticket?.satisfaction_comment) && (
          <div className="ui-row ui-gap-12 ui-wrap" style={{
            flexShrink: 0,
            padding: 'var(--ui-sp-8) var(--ui-sp-16)',
            borderBottom: '1px solid var(--ui-border)',
            background: 'var(--ui-surface-2)',
          }}>
            <Badge tone="medium" icon={HiStar}>
              Baholash: {ticket.satisfaction_score ? `${ticket.satisfaction_score}/5` : 'Baholanmagan'}
            </Badge>
            {ticket.satisfaction_comment && (
              <span className="ui-grow ui-row ui-gap-4" style={{ fontSize: 'var(--ui-fs-xs)', color: 'var(--ui-text-2)' }}>
                <HiChatBubbleLeftRight aria-hidden="true" />
                Fikr va taklif: "{ticket.satisfaction_comment}"
              </span>
            )}
          </div>
        )}

        {/* Xabarlar */}
        <div className="chat-wrap" style={{ flex: 1, border: 'none', borderRadius: 0 }}>
          {messages.length === 0
            ? <div className="ui-center ui-muted" style={{ padding: 'var(--ui-sp-32)' }}>Xabarlar yo'q</div>
            : messages.map((m, idx) => {
                const isCit = m.type === 'citizen' || m.type === 'system'
                const isInternal = m.type === 'internal'
                const prevMsg = idx > 0 ? messages[idx - 1] : null
                const isSameSender = prevMsg && prevMsg.type === m.type && prevMsg.user_name === m.user_name
                const showSenderName = !isSameSender
                const timeStr = new Date(m.created_at).toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' })
                const displayName = m.user_name || (isCit ? 'Fuqaro' : 'Operator')
                // Admin ko'rinishida "Fayl:" izohli tanalar ko'rsatilmaydi — fayl o'zi ko'rinadi
                const hideBody = isAdmin && m.body?.startsWith('Fayl:')
                const showBody = Boolean(m.body) && !m.body.startsWith('[') && !hideBody
                const align = isCit ? 'flex-start' : 'flex-end'
                return (
                  <div key={m.id} className="ui-col" style={{ width: '100%', alignItems: align }}>
                    {showSenderName && (
                      <div style={{
                        marginTop: idx > 0 ? 'var(--ui-sp-12)' : 0,
                        marginBottom: 'var(--ui-sp-4)',
                        fontSize: 'var(--ui-fs-2xs)',
                        fontWeight: 500,
                        color: 'var(--ui-text-3)',
                      }}>
                        {displayName}
                      </div>
                    )}

                    {m.ai_meta && <AiMetaNote meta={m.ai_meta} align={align} />}

                    <div className={`msg-row ${isCit ? 'citizen' : 'operator'}`}>
                      <div className={`msg-bubble ${isInternal ? 'msg-internal' : isCit ? 'msg-citizen' : 'msg-operator'}`}>
                        {showBody && <RichText text={m.body} />}
                        {m.files?.map(f => <FilePreview key={f.id} file={f} />)}
                        {isAdmin && !m.files?.length && m.body?.startsWith('Fayl:') && (
                          <span style={{ fontSize: 'var(--ui-fs-xs)' }}>Fayl yuklandi</span>
                        )}
                      </div>
                      <div className="msg-meta ui-nums">{timeStr}</div>
                    </div>

                    {showBody && <TelegramEmbeds text={m.body} isCit={isCit} />}
                  </div>
                )
              })
          }
          <div ref={bottomRef} />
        </div>

        {/* Yuborish paneli */}
        <div className="ui-col ui-gap-8" style={{
          flexShrink: 0,
          padding: 'var(--ui-sp-12)',
          borderTop: '1px solid var(--ui-border)',
        }}>
          {notice && (
            <Alert tone="danger" onClose={() => setNotice(null)}>{notice}</Alert>
          )}

          {uploading && (
            <div className="ui-row ui-gap-8" style={{ fontSize: 'var(--ui-fs-xs)', color: 'var(--ui-text-2)' }}>
              <Spinner size={14} />
              <span>{uploadProgress}</span>
            </div>
          )}

          {/* Telegram havolasi ko'rinishi */}
          {firstTg && (
            <div className="ui-row ui-gap-12" style={{
              padding: 'var(--ui-sp-8)',
              border: '1px solid var(--ui-border)',
              borderRadius: 'var(--ui-r)',
              background: 'var(--ui-surface-2)',
            }}>
              <div className="ui-grow">
                <div style={{
                  fontSize: 'var(--ui-fs-2xs)',
                  fontWeight: 500,
                  letterSpacing: '.06em',
                  textTransform: 'uppercase',
                  color: 'var(--ui-text-2)',
                }}>
                  Telegram post ko'rinishi
                </div>
                <div className="ui-truncate ui-mono" style={{ fontSize: 'var(--ui-fs-xs)', color: 'var(--ui-text-3)' }}>
                  t.me/{firstTg.channel}/{firstTg.postId}
                </div>
              </div>
              <div style={{
                width: 100,
                height: 48,
                overflow: 'hidden',
                border: '1px solid var(--ui-border)',
                borderRadius: 'var(--ui-r)',
                background: 'var(--ui-surface)',
              }}>
                <iframe
                  title="Telegram post"
                  src={`https://t.me/${firstTg.channel}/${firstTg.postId}?embed=1&userpic=false`}
                  style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                  scrolling="no"
                />
              </div>
            </div>
          )}

          {recording ? (
            <div className="ui-between ui-gap-12">
              <span className="ui-row ui-gap-8">
                <Dot tone="busy" label="Yozilmoqda" />
                <span className="ui-mono ui-nums" style={{ fontSize: 'var(--ui-fs-sm)' }}>
                  Yozilmoqda... {fmtSec}
                </span>
              </span>
              <Button variant="primary" icon={HiPaperAirplane} onClick={stopRecording}>Yuborish</Button>
            </div>
          ) : (
            <div className="ui-row ui-gap-8" style={{ alignItems: 'flex-end' }}>
              {!isAdmin && (
                <>
                  <input ref={fileInputRef} type="file" style={{ display: 'none' }}
                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar"
                    onChange={handleFile} disabled={locked} />
                  <Button
                    icon={HiPaperClip} iconOnly aria-label="Fayl yuklash"
                    onClick={() => !locked && fileInputRef.current?.click()}
                    disabled={uploading || locked}
                  />
                  <Button
                    icon={HiMicrophone} iconOnly aria-label="Ovoz xabari yozish"
                    onClick={startRecording}
                    disabled={uploading || locked}
                  />
                </>
              )}

              <Textarea
                ref={textareaRef}
                className="ui-grow"
                rows={1}
                value={reply}
                onChange={e => setReply(e.target.value)}
                disabled={locked}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !locked) { e.preventDefault(); send() } }}
                placeholder={locked ? "Muloqot yakunlangan («Bajarildi»), xabar yozib bo'lmaydi" : 'Xabar yozing... (Enter)'}
                style={{ minHeight: 32, maxHeight: 120, resize: 'none', overflow: 'hidden' }}
              />

              <Button
                variant="primary" icon={HiPaperAirplane} iconOnly aria-label="Yuborish"
                onClick={send} loading={sending}
                disabled={!reply.trim() || uploading || locked}
              />
            </div>
          )}
        </div>
      </div>

      {/* Navbat holati — faqat operator uchun */}
      {!isAdmin && pendingCount > 0 && (
        <div className="ui-between ui-gap-12 ui-wrap" style={{
          padding: 'var(--ui-sp-8) var(--ui-sp-16)',
          border: '1px solid var(--ui-border)',
          borderRadius: 'var(--ui-r-lg)',
          background: 'var(--ui-surface)',
        }}>
          <span className="ui-row ui-gap-8" style={{ fontSize: 'var(--ui-fs-sm)' }}>
            <HiClock aria-hidden="true" />
            Navbatda: <strong className="ui-nums">{pendingCount}</strong> ta murojaat
          </span>
          <span className="ui-muted" style={{ fontSize: 'var(--ui-fs-xs)' }}>
            Chatni tugating va keyingisini oling
          </span>
        </div>
      )}
    </div>
  )
}

// ── Asosiy komponent ────────────────────────────────────────────
export default function TicketDetail() {
  const { id }   = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const isAdmin  = user?.role === 'super_admin'
  const { triggerCriticalReload, setSplitPage, splitPage, addToast } = useStore()

  // Murojaatga kirilganda o'ng tomondagi FAQ paneli o'zi ochiladi
  useEffect(() => {
    if (!splitPage) setSplitPage('faq')
  }, [])

  const [ticket, setTicket]     = useState(null)
  const [messages, setMessages] = useState([])
  const [operators, setOperators] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState(null)
  const [pendingCount, setPending] = useState(0)
  const [showCloseModal, setShowCloseModal] = useState(false)
  const [closing, setClosing]   = useState(false)

  const load = useCallback(async () => {
    if (!id || id === 'undefined') { setError('Ticket ID xato'); setLoading(false); return }
    setLoading(true); setError(null)
    try {
      const [t, , cats] = await Promise.all([
        ticketsApi.getFull(id),
        templatesApi.list(),
        categoriesApi.list(),
      ])
      setTicket(t)
      setMessages(t.messages || [])
      setCategories(cats || [])

      if (isAdmin) {
        const ops = await operatorsApi.list()
        setOperators(ops)
      }

      // Kutilayotganlar soni
      const pending = await ticketsApi.list({ unassigned: 'true', limit: 1 })
      setPending(pending?.total || 0)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [id, isAdmin])

  useEffect(() => { load() }, [load])

  // FAQ yordamchi paneliga joriy chat kontekstini uzatish
  useEffect(() => {
    const lastCitizen = [...messages].reverse()
      .find(m => !m.user_id && (m.message_type === 'incoming' || m.message_type === 'text' || true))
    const ctx = ticket ? {
      id: ticket.id,
      number: ticket.ticket_number,
      subject: ticket.subject,
      lang: ticket.citizen?.language_code,
      categoryId: ticket.category_id || null,
      categoryName: (categories.find(c => String(c.id) === String(ticket.category_id))?.name_uz) || null,
      lastCitizen: lastCitizen?.body || ticket.description || '',
    } : null
    window.__ticketCtx = ctx
    window.dispatchEvent(new CustomEvent('ticket:context', { detail: ctx }))
    return () => {
      window.__ticketCtxCleanup = setTimeout(() => {
        window.__ticketCtx = null
        window.dispatchEvent(new CustomEvent('ticket:context', { detail: null }))
      }, 100)
    }
  }, [ticket, messages, categories])

  // 10 soniyada xabarlarni yangilash (faqat o'zgargan bo'lsa)
  useEffect(() => {
    if (!id || id === 'undefined') return
    let pollCtrl = null
    const timer = setInterval(async () => {
      // Oldingi sekin so'rov yangi holatni ustidan yozmasligi uchun bekor qilamiz
      pollCtrl?.abort()
      pollCtrl = new AbortController()
      const ctrl = pollCtrl
      try {
        const msgs = await ticketsApi.getMessages(id, { signal: ctrl.signal })
        if (ctrl.signal.aborted) return
        setMessages(prev => {
          if (prev.length !== msgs.length) return msgs
          const prevLast = prev[prev.length - 1]
          const newLast = msgs[msgs.length - 1]
          if (prevLast?.id !== newLast?.id) return msgs
          if (prevLast?.body !== newLast?.body) return msgs

          // Xabarlardagi fayllarning vt_status i o'zgarganini tekshirish
          const prevVt = JSON.stringify(prev.map(m => m.files?.map(f => f.vt_status)))
          const newVt = JSON.stringify(msgs.map(m => m.files?.map(f => f.vt_status)))
          if (prevVt !== newVt) return msgs

          return prev
        })
      } catch {}
    }, 10000)
    return () => { clearInterval(timer); pollCtrl?.abort() }
  }, [id])

  const handleSend = async (text) => {
    if (text === '__reload__') {
      const msgs = await ticketsApi.getMessages(id)
      setMessages(msgs)
      return
    }

    if (ticket?.status === 'resolved' || ticket?.status === 'closed') {
      addToast("Muloqot yakunlangan («Bajarildi»), xabar yozib bo'lmaydi", 'critical')
      return
    }

    if (ticket?.citizen?.language_code) {
      const check = validateMessageLanguage(text, ticket.citizen.language_code)
      if (!check.valid) {
        addToast(check.error, 'critical')
        throw new Error(check.error)
      }
    }

    await ticketsApi.sendMessage(id, { body: text, message_type: 'operator', send_to_telegram: true })
    const msgs = await ticketsApi.getMessages(id)
    setMessages(msgs)
  }

  const handleUpdate = async (body) => {
    await ticketsApi.update(id, body)
    await load()
  }

  const handleClose = () => setShowCloseModal(true)

  const doClose = async () => {
    setClosing(true)
    try {
      // Xayrlashuv xabari (rahmat + kanalga obuna tugmasi) backend tomonidan
      // fuqaroning o'z tilida avtomatik yuboriladi
      await ticketsApi.update(id, { status: 'resolved' })
      await new Promise(r => setTimeout(r, 300))
      triggerCriticalReload()
      const isCritical = ticket?.priority === 'critical'
      navigate(isCritical ? '/kritik' : '/data')
    } catch {
      navigate('/data')
    } finally {
      setClosing(false)
      setShowCloseModal(false)
    }
  }

  // Sahifa darajasidagi kutish — kichik Spinner emas, kitning Loader'i
  if (loading) return (
    <div className="ui-center" style={{ height: 300 }}>
      <Loader state="working" size="lg" block />
    </div>
  )

  if (error || !ticket) return (
    <div className="page-enter ui-col ui-gap-16">
      <div>
        <Button icon={HiArrowLeft} onClick={() => navigate('/tickets')}>Orqaga</Button>
      </div>
      <Alert tone="danger" title="Xatolik">{error || 'Ticket topilmadi'}</Alert>
    </div>
  )

  return (
    <div className="page-enter">
      <ConfirmModal
        open={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={doClose}
        title="Suhbatni yakunlash"
        text="Fuqaroga yakuniy xabar yuboriladi va suhbat ma'lumotlar bazasiga arxivlanadi."
        confirmLabel="Ha, yakunlash"
        loading={closing}
      />

      {/* Sarlavha — faqat admin uchun: orqaga, raqam, yangilash */}
      {isAdmin && (
        <div className="ui-between ui-gap-12" style={{ marginBottom: 'var(--ui-sp-16)' }}>
          <div className="ui-row ui-gap-12">
            <Button icon={HiArrowLeft} onClick={() => navigate('/tickets')}>Orqaga</Button>
            <span className="ui-mono" style={{ fontSize: 'var(--ui-fs-md)', fontWeight: 600 }}>
              {ticket.ticket_number}
            </span>
          </div>
          <Button icon={HiArrowPath} iconOnly aria-label="Yangilash" onClick={load} />
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--ui-sp-16)', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 380px', minWidth: 0 }}>
          <ChatView
            ticket={ticket}
            messages={messages}
            isAdmin={isAdmin}
            onSend={handleSend}
            onCloseChat={handleClose}
            pendingCount={pendingCount}
            ticketId={id}
          />
        </div>
        <aside style={{ flex: '0 1 260px', minWidth: 220 }}>
          <TicketSidebar
            ticket={ticket}
            categories={categories}
            operators={operators}
            isAdmin={isAdmin}
            onUpdate={handleUpdate}
          />
        </aside>
      </div>
    </div>
  )
}
